import { Box, SvgIcon } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { keyframes } from '@mui/system';
import { ReactComponent as Auto } from 'assets/images/icons/Auto.svg';
import { useEffect } from 'react';
import { brandColors } from 'types/colors';
import { modeValueStyle } from '../styles/style.js';
import { ReactComponent as Fan } from 'assets/images/icons/fan-speed.svg';

// SVG icon for all three spinning states
import { ReactComponent as FreshAirIcon } from 'assets/images/icons/Ventilation_Level.svg';

// Clockwise rotation keyframes (generic)
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Helper map for the four visual states
const levels = [
  { key: 'Low', value: 'low', duration: '3s' },
  { key: 'Medium', value: 'medium', duration: '1.1s' },
  { key: 'High', value: 'high', duration: '.6s' },
  { key: 'Auto', value: 'auto', duration: null },
];
const fourOptionSelectorStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  width: '11rem',
  ml: 1.5,
  gap: '0.6rem',
  flex: '0 0 11rem',
};

/**
 *  VentilationLevelSelector
 *
 *  Props
 *  ────────────────────────────────────────────────────────────────────────────
 *  currLevel : string   ─ external level ("Low" | "Medium" | "High" | "Auto")
 *  control   : string   ─ if === "manual" ⇒ user can change level
 *
 *  Behaviour
 *  ────────────────────────────────────────────────────────────────────────────
 *  • When control !== "manual" the component is read‑only; `currLevel` dictates
 *    which box is highlighted.
 *  • When control === "manual" the user can click any box to select it.  The
 *    internal `selected` state is used for the highlight.  External changes to
 *    `currLevel` are ignored as long as manual control is active (mirroring the
 *    pattern used in ModeSelector).
 */
export default function VentilationLevelSelector({
  control,
  unitOnOffStatus,
  selectedFanSpeed,
  setSelectedFanSpeed,
  onManualSelect,
  hideAutoOption = false,
}) {
  // keep in sync when manual mode is OFF
  useEffect(() => {
    if (control !== 'manual') {
      setSelectedFanSpeed(selectedFanSpeed);
    }
  }, [selectedFanSpeed, control]);

  const isActive = level =>
    control === 'manual' ? selectedFanSpeed === level : selectedFanSpeed === level;
  const visibleLevels = hideAutoOption ? levels.filter(({ key }) => key !== 'Auto') : levels;

  return (
    <Box
      sx={fourOptionSelectorStyle}>
      {visibleLevels.map(({ key, value, duration }, idx) => (
        <Tooltip
          title={
            unitOnOffStatus === false ? (
              <div style={{ textAlign: 'center' }}>
                {key}
                <br />
                <p style={{ fontSize: '.8rem' }}>unit is currently OFF</p>
              </div>
            ) : (
              key
            )
          }
          placement="bottom">
          <Box
            key={key}
            onClick={
              control === 'manual'
                ? () => {
                    onManualSelect?.(value);
                    setSelectedFanSpeed(value);
                  }
                : undefined
            }
            sx={{
              ...modeValueStyle,
              cursor: control === 'manual' ? 'pointer' : 'default',
              border: isActive(value)
                ? unitOnOffStatus === false
                  ? `2px solid ${brandColors.GREY}`
                  : `2px solid ${brandColors.BLACK}`
                : `1px solid ${brandColors.GREY_LIGHT}`,
              transition: 'transform 0.2s ease',
              '&:hover':
                control === 'manual'
                  ? {
                      transform: 'scale(1.07)',
                      boxShadow: '0px 0px 5px 0px #000000',
                    }
                  : {},
            }}>
            {key !== 'Auto' ? (
              <SvgIcon
                component={Fan}
                inheritViewBox
                sx={{
                  scale: '1.4',
                  color:
                    unitOnOffStatus === false
                      ? isActive(value)
                        ? brandColors.GREY_DARK
                        : brandColors.GREY
                      : isActive(value)
                        ? brandColors.BLACK
                        : brandColors.GREY_DARK,
                  animation: `${spin} ${duration} linear infinite`,
                }}
              />
            ) : (
              <SvgIcon
                component={Auto}
                inheritViewBox
                sx={{
                  scale: '2',
                  color:
                    unitOnOffStatus === false
                      ? isActive(value)
                        ? brandColors.GREY_DARK
                        : brandColors.GREY
                      : isActive(value)
                        ? brandColors.BLACK
                        : brandColors.GREY_DARK,
                }}
              />
            )}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}
